import { CheckCheck } from "lucide-react";
import { toast } from "sonner";
import { trpc } from "@/lib/trpc";
import { LinhaDoConvite } from "./LinhaDoConvite";

/** Convites de equipe pendentes endereçados ao email da conta logada — aceitar/recusar direto
 *  na plataforma, sem depender do link do email (o link continua funcionando também).
 *
 *  Convite feito pra várias organizações de uma vez chega junto: dá pra aceitar todas num clique
 *  ou responder uma por uma. */
export function PendingOrgInvitesList() {
  const utils = trpc.useUtils();
  const invitesQuery = trpc.team.invite.listMine.useQuery();

  const atualizar = () => {
    utils.team.invite.listMine.invalidate();
    utils.notification.invalidate();
  };
  const accept = trpc.team.invite.accept.useMutation({
    onSuccess: (result, vars) => {
      if (result.requiresSignup) return;
      toast.success(vars.apenasEste === false ? "Convites aceitos." : "Convite aceito — organização adicionada à sua conta.");
      atualizar();
      utils.me.current.invalidate();
      utils.organization.listMine.invalidate();
    },
    onError: (e) => toast.error(e.message || "Erro ao aceitar convite."),
  });
  const decline = trpc.team.invite.declineMine.useMutation({
    onSuccess: () => {
      toast.success("Convite recusado.");
      atualizar();
    },
    onError: (e) => toast.error(e.message || "Erro ao recusar convite."),
  });

  const invites = invitesQuery.data ?? [];
  if (invites.length === 0) return null;

  // Agrupa pelo envio: convite sem grupo é um envio sozinho.
  const grupos = new Map<string, typeof invites>();
  for (const inv of invites) {
    const chave = inv.grupoId ?? inv.id;
    grupos.set(chave, [...(grupos.get(chave) ?? []), inv]);
  }
  const ocupado = accept.isPending || decline.isPending;

  return (
    <div className="mb-4 rounded-lg border border-brand-200 bg-brand-50/50 dark:border-primary/30 dark:bg-primary/5">
      <div className="flex items-center justify-between gap-2 border-b border-brand-200/60 px-3 py-2 dark:border-primary/20">
        <p className="text-[12px] font-semibold text-brand-900 dark:text-primary">
          {invites.length} convite{invites.length > 1 ? "s" : ""} de organização pendente
          {invites.length > 1 ? "s" : ""}
        </p>
      </div>
      <div className="divide-y divide-brand-200/60 dark:divide-primary/20">
        {[...grupos.entries()].map(([chave, doGrupo]) => (
          <div key={chave}>
            {doGrupo.length > 1 && (
              <div className="flex flex-wrap items-center justify-between gap-2 bg-brand-100/40 px-3 py-1.5 dark:bg-primary/10">
                <p className="text-[11px] font-medium text-zinc-600 dark:text-muted-foreground">
                  Convite para {doGrupo.length} organizações
                </p>
                <button
                  type="button"
                  onClick={() => accept.mutate({ token: doGrupo[0].token, apenasEste: false })}
                  disabled={ocupado}
                  className="inline-flex h-7 items-center gap-1 rounded-md bg-emerald-600 px-2.5 text-[11px] font-semibold text-white transition-colors hover:bg-emerald-700 disabled:opacity-60"
                >
                  <CheckCheck className="h-3.5 w-3.5" />
                  Aceitar todas ({doGrupo.length})
                </button>
              </div>
            )}
            <ul className="divide-y divide-brand-200/40 dark:divide-primary/15">
              {doGrupo.map((inv) => (
                <LinhaDoConvite
                  key={inv.id}
                  convite={inv}
                  ocupado={ocupado}
                  onAceitar={() => accept.mutate({ token: inv.token, apenasEste: true })}
                  onRecusar={() => decline.mutate({ token: inv.token })}
                />
              ))}
            </ul>
          </div>
        ))}
      </div>
    </div>
  );
}
